import React, { useState } from 'react';

export const PILLAR_THEME = {
  1: { primary: '#0D5C46', accent: '#10B981', bg: '#ECFDF5', label: 'Decision Ripple' },
  2: { primary: '#2563EB', accent: '#3B82F6', bg: '#EFF6FF', label: 'Culture Signal' },
  3: { primary: '#D97706', accent: '#F59E0B', bg: '#FFFBEB', label: 'Product Journey' },
  4: { primary: '#0891B2', accent: '#06B6D4', bg: '#ECFEFF', label: 'Feedback Loop' },
  5: { primary: '#7C3AED', accent: '#8B5CF6', bg: '#F5F3FF', label: 'Impact Chain' },
  6: { primary: '#E11D48', accent: '#F43F5E', bg: '#FFF1F2', label: 'Promise → Proof' },
};

const MAPS = {
  decision_ripple: {
    title: 'The Leader\u2019s Ripple Effect',
    intro: 'One decision, four sets of consequences. Select a ring to see what it carries.',
    centre: 'The decision',
    nodes: [
      { label: 'Employees', detail: 'Do people learn what the organisation actually rewards \u2014 candour or compliance?' },
      { label: 'Customers', detail: 'Does the decision keep the promise the customer already bought?' },
      { label: 'Partners', detail: 'Who absorbs the risk or cost that we removed from our own books?' },
      { label: 'Planet & society', detail: 'What consequence lands outside our boundary, and on whom?' },
    ],
    layout: 'rings',
  },
  culture_signal: {
    title: 'Culture Signal',
    intro: 'Culture is read from small, repeated signals. Select one to see what it teaches.',
    centre: 'Daily signals',
    nodes: [
      { label: 'Speak', detail: 'Can someone raise a problem on an ordinary Tuesday without rehearsing it first?' },
      { label: 'Learn', detail: 'Are mistakes studied, or quietly absorbed by whoever made them?' },
      { label: 'Recognise', detail: 'Is praise specific enough to prove someone was watching the work?' },
      { label: 'Grow', detail: 'Does development exist for people who are not already visible?' },
    ],
    layout: 'grid',
  },
  product_journey: {
    title: 'Product Journey',
    intro: 'Every stage sets a consequence the next stage inherits.',
    centre: 'Lifecycle',
    nodes: [
      { label: 'Idea', detail: 'Whose problem is this, and is it worth solving?' },
      { label: 'Design', detail: 'Safety, accessibility and repairability are decided here, cheaply.' },
      { label: 'Make', detail: 'Materials and suppliers become part of the product\u2019s story.' },
      { label: 'Use', detail: 'Does the user actually accomplish the task we promised?' },
      { label: 'Repair', detail: 'Can it be kept alive, by whom, at what cost?' },
      { label: 'End / Next', detail: 'What happens to it afterwards \u2014 and who pays?' },
    ],
    layout: 'journey',
  },
  feedback_loop: {
    title: 'Promise \u2192 Experience Loop',
    intro: 'Trust is repaired or eroded on every pass through this loop.',
    centre: 'Trust',
    nodes: [
      { label: 'Promise', detail: 'What we claimed sets the standard we are judged against.' },
      { label: 'Experience', detail: 'What the customer actually met, including the difficult parts.' },
      { label: 'Listen', detail: 'Complaints are free research with examples attached.' },
      { label: 'Fix', detail: 'Remove the cause, not just the ticket.' },
      { label: 'Return', detail: 'Do they come back, and do they bring anyone?' },
    ],
    layout: 'loop',
  },
  impact_chain: {
    title: 'Impact Chain',
    intro: 'Most reporting stops two links early. Select a link to see the question it answers.',
    centre: 'What changed?',
    nodes: [
      { label: 'Input', detail: 'What we spent \u2014 money, hours, product.' },
      { label: 'Activity', detail: 'What we ran, and with whom.' },
      { label: 'Output', detail: 'Who turned up or received something.' },
      { label: 'Outcome', detail: 'What changed for those people.' },
      { label: 'Impact', detail: 'What is still true a year later.' },
    ],
    layout: 'journey',
  },
  promise_proof: {
    title: 'Promise \u2192 Proof',
    intro: 'A claim is only as strong as the behaviour behind it.',
    centre: 'Credibility',
    nodes: [
      { label: 'Promise', detail: 'The public claim, in the words a customer would repeat.' },
      { label: 'Practice', detail: 'The internal behaviour that must already be true.' },
      { label: 'Partners', detail: 'What suppliers would say about working with us.' },
      { label: 'Evidence', detail: 'The record, the owner and the date.' },
      { label: 'Experience', detail: 'What people meet when they test the claim.' },
    ],
    layout: 'journey',
  },
};

/** Per-pillar conceptual diagram with selectable nodes. */
export const VisualMap = ({ type, pillarNumber }) => {
  const map = MAPS[type] || MAPS.decision_ripple;
  const theme = PILLAR_THEME[pillarNumber] || PILLAR_THEME[1];
  const [active, setActive] = useState(0);

  const isJourney = map.layout === 'journey';

  return (
    <div data-testid="visual-map">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: theme.primary }}>
        {theme.label}
      </p>
      <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mb-3">{map.title}</h3>
      <p className="text-base text-slate-600 mb-8 max-w-2xl">{map.intro}</p>

      <div className="rounded-2xl p-6 sm:p-8" style={{ backgroundColor: theme.bg }}>
        {isJourney ? (
          <div className="flex flex-wrap items-center gap-2">
            {map.nodes.map((node, i) => (
              <React.Fragment key={node.label}>
                <button
                  onClick={() => setActive(i)}
                  data-testid={`visual-map-node-${i}`}
                  className={`px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    active === i ? 'text-white shadow-lg scale-[1.03]' : 'bg-white/80 text-slate-700 hover:bg-white'
                  }`}
                  style={active === i ? { backgroundColor: theme.primary } : {}}
                >
                  {node.label}
                </button>
                {i < map.nodes.length - 1 && <span className="text-slate-400">→</span>}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div
              className="relative w-52 h-52 shrink-0 rounded-full flex items-center justify-center text-center"
              style={{ border: `2px dashed ${theme.accent}` }}
            >
              <div
                className="w-28 h-28 rounded-full flex items-center justify-center text-white text-sm font-semibold px-3 text-center"
                style={{ backgroundColor: theme.primary }}
              >
                {map.centre}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 flex-1 w-full">
              {map.nodes.map((node, i) => (
                <button
                  key={node.label}
                  onClick={() => setActive(i)}
                  data-testid={`visual-map-node-${i}`}
                  className={`px-4 py-4 rounded-xl text-left text-sm font-semibold transition-all duration-200 ${
                    active === i ? 'text-white shadow-lg' : 'bg-white/80 text-slate-700 hover:bg-white'
                  }`}
                  style={active === i ? { backgroundColor: theme.primary } : {}}
                >
                  {node.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div
          className="mt-6 bg-white rounded-xl p-5 border-l-4 transition-all duration-300"
          style={{ borderColor: theme.primary }}
          data-testid="visual-map-detail"
        >
          <p className="text-sm font-bold text-slate-900 mb-1">{map.nodes[active].label}</p>
          <p className="text-base text-slate-600 leading-relaxed">{map.nodes[active].detail}</p>
        </div>
      </div>
    </div>
  );
};

export default VisualMap;
